<template>
  <div
      class="scrollable-machines-container"
      :style="{ '--planning-days': planningDays }"
      ref="machinesScrollEl"
      @dragover.prevent="onMachinesDragOver"
    >

    <div class="stacked-machines">

      <!-- Display-only calendar machine -->
      <div class="machine-row date-machine-row">
        <div class="machine-header">
          <div class="machine-name">Date</div>
        </div>

        <!-- Must match the real draggable wrapper -->
        <div class="machine-order-list">
          <div class="work-order date-work-order">
            <div class="order-info">

              <!-- Matches the item / quantity / WO column -->
              <div class="order-details date-order-placeholder"></div>

              <!-- Matches the real R boxes exactly -->
              <div class="day-boxes">
                <div
                  v-for="label in planningDateLabels"
                  :key="label.dayNumber"
                  class="day-box date-day-box"
                >
                  <span class="planning-date-day">
                    {{ label.dayName }}
                  </span>

                  <span class="planning-date-value">
                    {{ label.dateLabel }}
                  </span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <div class="machine-row" v-for="machine in localMachines" :key="machine.id">
        <div class="machine-header">
          <div class="machine-name">{{ machine.name }}</div>

          <!-- Entire section as one vertical column -->
          <div class="machine-body">
            

            <!-- Employee Drop Container (Operator and Boxer stacked vertically below comments) -->
            <div class="employee-drop-container">
              <button
                class="notes-btn"
                @click.stop="$emit('open-machine-notes', machine)"
              >
                Notes
              </button>

              <div class="machine-override">
                <label class="override-label">Items/day</label>
                <select
                  class="override-select"
                  :value="machineItemLimitOverrides[machine.name] ?? null"
                  @change="onChangeMachineLimit($event, machine.name)"
                >
                  <option :value="''">Default</option>
                  <option v-for="n in 12" :key="n" :value="n">{{ n }}</option>
                </select>
              </div>
              
              <!-- Resources Button + Summary (replaces operator/boxer drop areas) -->
              <div class="resources-block">
              <button
                class="notes-btn"
                @click.stop="$emit('open-machine-resources', machine)"
              >
                Resources
              </button>

              <div class="resources-summary">
                <div>Operator: <strong>{{ machine.operator?.employee_name || '—' }}</strong></div>
                <div>Boxer: <strong>{{ machine.boxer?.employee_name || '—' }}</strong></div>
              </div>
            </div>


              <!-- NEW: Machine Load Button -->
              <button
                class="notes-btn"
                @click.stop="$emit('show-machine-load', { machineName: machine.name })"
              >
                Hours / Load
              </button>

              <button
                class="notes-btn"
                @click.stop="openMachineHoursMaintenance(machine)"
              >
                Input Hours
              </button>

              <button
                class="lock-btn"
                :class="machine.locked ? 'lock-red' : 'lock-green'"
                @click="$emit('toggle-machine-lock', machine.id)"
                title="Locking saves and prevents future changes"
              >
                {{ machine.locked ? '🔒' : '🔓' }}
              </button>

              <button
                class="notes-btn"
                :disabled="!machine.locked"
                :title="machine.locked ? 'Fill run quantities to machine capacity' : 'Lock this machine before filling runs'"
                @click.stop="$emit('fill-machine-runs', machine)"
              >
                Fill Runs
              </button>


            </div>
          </div>
        </div>



        <!-- ⭐ NEW: Machine Load / Capacity Modal -->
<div
  v-if="showMachineLoadModal && selectedMachineLoad"
  class="shortage-modal-backdrop machine-load-backdrop"
>
  <div class="shortage-modal">
    <div class="shortage-modal-header">
      <h3>
        Capacity – {{ selectedMachineLoad.machine }}
      </h3>
      <button class="close-btn" @click="$emit('close-machine-load-modal')">✕</button>
    </div>

    <div class="shortage-modal-body">
      <p>
        <strong>Location:</strong> {{ selectedMachineLoad.loc }}<br />
        <strong>Normal Hours/Day:</strong> {{ selectedMachineLoad.allowed_hours ?? 0 }}
      </p>

      <!-- Loading state -->
      <div v-if="selectedMachineLoad.loading">
        <em>Loading capacity…</em>
      </div>

      <!-- Error state -->
      <div v-else-if="selectedMachineLoad.error">
        <em>{{ selectedMachineLoad.error }}</em>
      </div>

      <!-- Empty state -->
      <div v-else-if="!selectedMachineLoad.rows || !selectedMachineLoad.rows.length">
        <em>No load rows found for this machine.</em>
      </div>

      <!-- Table -->
      <table v-else class="shortage-table">
        <thead>
          <tr>
            <th>Day</th>
            <th>Planned Hours</th>
            <th>Allowed</th>
            <th>Util %</th>
            <th>Planned Qty</th>
            <th>Missing Rates</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="(r, idx) in selectedMachineLoad.rows"
            :key="idx"
          >
            <td>D {{ r.day_num }}</td>
            <td>{{ formatHours(r.planned_hours) }}</td>
            <td>{{ formatHours(r.allowed_hours) }}</td>
            <td>{{ formatPct(r.utilization_pct) }}</td>
            <td>{{ formatQty(r.qty_scheduled) }}</td>
            <td>
              {{ (r.missing_rate_rows ?? 0) + (r.zero_or_bad_rate_rows ?? 0) }}
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Optional warning -->
      <div
        v-if="selectedMachineLoad.rows && selectedMachineLoad.rows.some(r => (r.utilization_pct ?? 0) > 100)"
        style="margin-top: 10px;"
      >
        <strong>⚠ Over capacity:</strong> One or more days exceed allowed hours.
      </div>
    </div>
  </div>
</div>

<!-- Machine Hours / Daily Capacity Maintenance Modal -->
<div
  v-if="showMachineHoursModal && selectedMachineHours"
  class="shortage-modal-backdrop"
>
  <div class="shortage-modal machine-hours-modal">
    <div class="shortage-modal-header">
      <h3>
        Machine Input Hours – {{ selectedMachineHours.machine }}
      </h3>

      <button
        class="close-btn"
        @click="closeMachineHoursMaintenance"
      >
        ✕
      </button>
    </div>

    <div class="shortage-modal-body">
      <p>
        <strong>Location:</strong>
        {{ selectedMachineHours.loc }}<br />

        <strong>Machine:</strong>
        {{ selectedMachineHours.machine }}
      </p>

      <!-- ================================================= -->
      <!-- NORMAL / DEFAULT MACHINE HOURS -->
      <!-- ================================================= -->
      <div class="machine-hours-section">
        <h4>Normal Capacity</h4>

        <p class="machine-hours-help">
          Normal hours are used unless a specific date has an override.
        </p>

        <div v-if="machineHoursLoading">
          <em>Loading normal machine hours…</em>
        </div>

        <div v-else>
          <label class="machine-hours-label">
            Normal Hours / Day
          </label>

          <div class="machine-hours-normal-row">
            <input
              class="machine-hours-input"
              type="number"
              step="0.25"
              min="0"
              max="24"
              v-model="selectedMachineHours.allowed_hours"
            />

            <button
              class="btn btn-small"
              @click="saveMachineHours"
              :disabled="machineHoursSaving"
            >
              {{
                machineHoursSaving
                  ? "Saving…"
                  : "Save Normal Hours"
              }}
            </button>
          </div>
        </div>
      </div>

      <!-- ================================================= -->
      <!-- DAILY / DATE-SPECIFIC CAPACITY -->
      <!-- ================================================= -->
      <div class="machine-hours-section machine-capacity-section">
        <h4>Daily Capacity</h4>

        <p class="machine-hours-help">
          Override individual production dates for holidays,
          shutdowns, Saturdays, overtime, or other schedule changes.
          A value of 0 means the machine is closed for that date.
        </p>

        <div v-if="machineCapacityLoading">
          <em>Loading daily capacity…</em>
        </div>

        <div
          v-else-if="!machineCapacityRows.length"
          class="machine-hours-empty"
        >
          <em>No daily capacity rows found.</em>
        </div>

        <table
          v-else
          class="shortage-table machine-capacity-table"
        >
          <thead>
            <tr>
              <th>Day</th>
              <th>Date</th>
              <th>Normal</th>
              <th>Daily Hours</th>
              <th>Source</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="row in machineCapacityRows"
              :key="`${row.machine}-${row.work_date}`"
            >
              <td>
                D {{ row.day_num }}
              </td>

              <td>
                {{ formatCapacityDate(row.work_date) }}
              </td>

              <td>
                {{ formatHours(row.default_allowed_hours) }}
              </td>

              <td>
                <input
                  class="machine-capacity-input"
                  type="number"
                  step="0.25"
                  min="0"
                  max="24"
                  v-model="row.edit_hours"
                  :disabled="row.saving"
                />
              </td>

              <td>
                <span
                  class="capacity-source-badge"
                  :class="{
                    'capacity-source-override':
                      row.is_override,
                    'capacity-source-normal':
                      !row.is_override
                  }"
                >
                  {{
                    row.is_override
                      ? "Override"
                      : "Normal"
                  }}
                </span>
              </td>

              <td>
                <div class="machine-capacity-actions">
                  <button
                    class="btn btn-small"
                    @click="saveMachineCapacityOverride(row)"
                    :disabled="row.saving"
                  >
                    {{
                      row.saving
                        ? "Saving…"
                        : "Save"
                    }}
                  </button>

                  <button
                    v-if="row.is_override"
                    class="btn btn-small"
                    @click="resetMachineCapacityOverride(row)"
                    :disabled="row.saving"
                  >
                    Reset
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="machineHoursError"
        class="machine-hours-error"
      >
        {{ machineHoursError }}
      </div>

      <div
        v-if="machineHoursSuccess"
        class="machine-hours-success"
      >
        {{ machineHoursSuccess }}
      </div>

      <div class="machine-hours-actions">
        <button
          class="btn btn-small"
          @click="closeMachineHoursMaintenance"
          :disabled="
            machineHoursSaving ||
            machineCapacitySaving
          "
        >
          Close
        </button>
      </div>
    </div>
  </div>
</div>

        <!-- ⭐ NEW: Sales Demand Modal (per D box) -->
<div
  v-if="showDayDemandModal && selectedDayDemand"
  class="shortage-modal-backdrop"
>
  <div class="shortage-modal">
    <div class="shortage-modal-header">
      <h3>
        Sales Demand – D {{ selectedDayDemand.dayIndex }}
      </h3>
      <button class="close-btn" @click="closeDayDemandModal">✕</button>
    </div>

    <div class="shortage-modal-body">
      <!-- Top summary -->
      <p>
        <strong>Item:</strong> {{ selectedDayDemand.order.partNumber }}<br />
        <strong>WO Qty (this day):</strong>
        {{ selectedDayDemand.order['D ' + selectedDayDemand.dayIndex] ?? 0 }}<br />

        <!-- ⭐ NEW: demand summary -->
        <strong>Sales Demand (this day):</strong>
          {{ selectedDayDemand.total_qty ?? 0 }}<br />
          <strong>SO Lines:</strong>
          {{ selectedDayDemand.line_count ?? 0 }}
        </p>

        <div v-if="moveOutReqError" style="color:#ff5c5c; margin-top:8px;">
          {{ moveOutReqError }}
        </div>

        <div v-if="moveOutReqSuccess" style="color:#56d364; margin-top:8px;">
          {{ moveOutReqSuccess }}
        </div>


        <!-- Loading state -->
        <div v-if="selectedDayDemand.loading">
          <em>Loading sales demand…</em>
        </div>

        <!-- Error state -->
        <div v-else-if="selectedDayDemand.error">
          <em>{{ selectedDayDemand.error }}</em>
        </div>

        <!-- Empty state -->
        <div v-else-if="!selectedDayDemand.rows || !selectedDayDemand.rows.length">
          <em>No sales orders in this planning day for this item.</em>
        </div>

        <!-- Table -->
        <table
          v-else
          class="shortage-table"
        >
          <thead>
            <tr>
              <th>SO #</th>
              <th>Customer Boo yeah</th>
              <th>Req Ship</th>
              <th>Qty</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(r, idx) in selectedDayDemand.rows"
              :key="idx"
            >
              <td>{{ r.ord_no }}</td>

              <td>
                {{ r.customer_name || ('Customer #' + r.customer_id) }}
                <span v-if="r.customer_city"> — {{ r.customer_city }}</span>
              </td>

              <td>{{ formatDate(r.req_ship_dt) }}</td>
              <td>{{ r.qty_ordered }}</td>
              <td>
                <button
                  class="btn btn-small"
                  @click.stop="requestLogisticsMoveOut(r)"
                  :disabled="sendingMoveOutReq"
                >
                  {{ sendingMoveOutReq ? "Sending…" : "Request Move Out" }}
                </button>

              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

    



        <!-- Draggable work orders -->
        <draggable
          class="machine-order-list"
          v-model="machine.orders"
          :group="{ name: 'orders', pull: true, put: canDrop(machine.name) }"
          item-key="orderId"
          :animation="150"
          ghost-class="wo-ghost"
          chosen-class="wo-chosen"
          drag-class="wo-drag"
          :move="canMoveOrder"
          @start="onDragStart"
          @end="onDragEnd"

        >
          <template #item="{ element }">
            <div
              class="work-order"
              :class="{
                'pending-work-order': isPendingOrder(element),
                'unreviewed-work-order': element.is_unreviewed
              }"
              :key="element.orderId"
              :data-part-number="element.partNumber"
              :data-ord-no="element.orderId"
              :data-ord-qty="element.ord_qty"
            >
                
              <div class="order-info">
                <div class="order-details">
                  <span
                    class="machine-item-number"
                    :class="getMachineItemClass(element)"
                  >
                    {{ element.partNumber }}
                  </span>

                  <span>{{ Math.round(checkOrdQty(element.ord_qty)) }}</span>

                  <span class="wo-label">
                    <strong>WO {{ element.orderId }}</strong>
                    <span
                      v-if="element.locked"
                      class="wo-lock-dot"
                      title="Locked work order"
                    ></span>
                  </span>

                  <!-- Only show this for partial / started work orders -->
                  <label
                    v-if="element.has_reported_prod"
                    class="keep-position-flag"
                    title="Keep this partial work order in the position selected by the scheduler"
                    @click.stop
                    @mousedown.stop
                  >
                    <input
                      type="checkbox"
                      :checked="element.keep_manual_sequence"
                      @click.stop
                      @mousedown.stop
                      @change.stop="onKeepManualSequenceChange($event, element)"
                    />

                    <span>Keep Position</span>
                  </label>

                  <span
                    v-if="isPendingOrder(element)"
                    class="pending-order-badge"
                    title="This order is in Pablo but has not yet been created in the ERP"
                  >
                    PENDING
                  </span>


                  <span
                    class="wo-label"
                    title="Quantity on hand"
                  >
                    <strong>OH {{ Math.round(Number(element.qoh ?? 0)).toLocaleString() }}</strong>
                  </span>


                  
                </div>

                <!-- 🧠 Run Plan Boxes (RUN) + MUST urgency -->
                <div class="day-boxes">
                  <div
                    class="day-box"
                    v-for="day in planningDays"
                    :key="'RUN ' + day"
                    :class="getDayBoxClass(machine, element, day)"
                    @click.stop="requestDayDemand(element, day)"
                    @dragover.prevent
                    @drop.stop="onRunDrop($event, element, day)"
                  >

                    <!-- ⠿ Drag handle (ONLY this starts drag) -->
                    <span
                      class="run-drag-handle"
                      draggable="true"
                      @dragstart.stop="onRunDragStart($event, element, day)"
                      @click.stop
                      title="Drag to move this run plan to another day"
                    >
                      ⠿
                    </span>

                    <!-- ✏️ Edit quantity (hover only) -->
                    <span
                      class="run-edit-icon"
                      @click.stop="onRunEditClick(element, day)"
                      title="Edit run quantity"
                    >
                      ✏️
                    </span>


                    <span class="day-label">R {{ day }}:</span>

                    <span class="day-value">
                      {{ Math.round(Number(element[`RUN ${day}`] || 0)) }}
                    </span>

                    <span
                      v-if="getFgShortDisplay(machine, element, day)"
                      class="fg-short-corner"
                      title="Finished goods shortage"
                    >
                      {{ getFgShortDisplay(machine, element, day) }}
                    </span>
                                        
                    <span
                      v-if="Number(element[`MUST ${day}`] || 0) === 1"
                      class="must-badge"
                    >
                    </span>

                  </div>
                </div>




                <!-- Material shortage indicators -->
                <div
                  v-if="getShortageIndicators(element).length"
                  class="shortage-indicator-group"
                >
                  <button
                    v-for="indicator in getShortageIndicators(element)"
                    :key="`${element.orderId}-${indicator}`"
                    type="button"
                    class="shortage-indicator-btn"
                    :class="{
                      'shortage-indicator-wide': indicator === 'Bx',
                      'shortage-indicator-unmapped': indicator === '?',
                      'shortage-indicator-wip':
                        indicator === 'C' || indicator === 'J'
                    }"
                    :title="getShortageIndicatorTitle(indicator)"
                    @click.stop="$emit(
                      'show-insufficiency-details',
                      {
                        item_no: element.partNumber,
                        ord_no: element.orderId
                      }
                    )"
                  >
                    {{ indicator }}
                  </button>
                </div>

                <!-- Work Order execution instructions -->
                <div
                  class="wo-execution-flags"
                  @click.stop
                  @mousedown.stop
                >
                  <label
                    class="wo-execution-flag"
                    :class="{ active: element.shift_changeover_1st }"
                    title="Shift changeover - 1st shift"
                  >
                    <input
                      type="checkbox"
                      :checked="Boolean(element.shift_changeover_1st)"
                      @click.stop
                      @mousedown.stop
                      @change.stop="
                        onWoExecutionFlagChange(
                          $event,
                          element,
                          'shift_changeover_1st'
                        )
                      "
                    />
                    <span>1st</span>
                  </label>

                  <label
                    class="wo-execution-flag"
                    :class="{ active: element.shift_changeover_2nd }"
                    title="Shift changeover - 2nd shift"
                  >
                    <input
                      type="checkbox"
                      :checked="Boolean(element.shift_changeover_2nd)"
                      @click.stop
                      @mousedown.stop
                      @change.stop="
                        onWoExecutionFlagChange(
                          $event,
                          element,
                          'shift_changeover_2nd'
                        )
                      "
                    />
                    <span>2nd</span>
                  </label>

                  <label
                    class="wo-execution-flag"
                    :class="{ active: element.exact_quantity }"
                    title="Run exact work order quantity"
                  >
                    <input
                      type="checkbox"
                      :checked="Boolean(element.exact_quantity)"
                      @click.stop
                      @mousedown.stop
                      @change.stop="
                        onWoExecutionFlagChange(
                          $event,
                          element,
                          'exact_quantity'
                        )
                      "
                    />
                    <span>Qty</span>
                  </label>
                </div>

              </div>
            </div>
                    </template>

                    <!-- Daily Jim totals -->
                    <template #footer>
                      <div class="machine-totals">
                        <div class="machine-total-qty">Total Qty:
                          <strong>{{ Math.round((machine.orders || []).reduce((sum, wo) => sum + Number(wo.ord_qty || 0), 0)).toLocaleString() }}</strong>
                        </div>
                        <div class="machine-total-row">
                          <div
                            class="machine-total-label"
                            title="Scheduled Quantity Total"
                          >
                            QT
                          </div>

                          <div class="day-boxes">
                            <div
                              v-for="day in planningDays"
                              :key="`qt-${machine.id}-${day}`"
                              class="machine-total-box qt-total-box"
                            >
                              {{
                                Math.round(
                                  getMachineScheduledQtyTotal(machine, day)
                                ).toLocaleString()
                              }}
                            </div>
                          </div>
                        </div>

                        <div class="machine-total-row">
                          <div
                            class="machine-total-label"
                            title="Short Quantity Total"
                          >
                            ST
                          </div>

                          <div class="day-boxes">
                            <div
                              v-for="day in planningDays"
                              :key="`st-${machine.id}-${day}`"
                              class="machine-total-box st-total-box"
                              :class="{
                                'has-shortage':
                                  getMachineShortQtyTotal(machine, day) > 0
                              }"
                            >
                              {{
                                Math.round(
                                  getMachineShortQtyTotal(machine, day)
                                ).toLocaleString()
                              }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>

                  </draggable>
      </div>
    </div>
    <div
      v-if="invalidDrop"
      class="invalid-drop-overlay"
      :style="{ left: invalidDropPosition.x + 'px', top: invalidDropPosition.y + 'px' }"
    >
      🚫
    </div>

  </div>
</template>

<script>
import {
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  computed
} from 'vue';
import draggable from 'vuedraggable';
import axios from 'axios';

export default {
  components: { draggable },
  emits: [
    'update-machine-item-limit',
    'show-machine-load',
    'close-machine-load-modal',
    'run-plan-move',
    'run-plan-set',
    'toggle-machine-lock',
    'fill-machine-runs',
    'open-machine-hours-maintenance',
    'show-insufficiency-details',
    'show-forecast-details',
    'show-day-demand',
    'open-machine-notes',
    'open-machine-resources',
    'mark-resource-dropped',
    'open-moveout-request',
    'machine-hours-updated',
    'schedule-resequenced',
    'schedule-resequence-failed',
    'set-keep-manual-sequence',
    'set-work-order-execution-flags'

  ],
  props: {
    machines: Array,
    resources: {
      type: Array,
      default: () => [] // Ensure resources defaults to an empty array
    },
    throughput: Object,
    showInsufficiencyDetails: Function,
    materialAlerts: {
      type: Array,
      default: () => []
    },
    unscheduledForecasts: {
    type: Array,
    default: () => []
    },
    planningDays: {
    type: Number,
    required: true
    },

    machineItemLimits: {
      type: Object,
      default: () => ({})
    },

    machineNotes: {
    type: Array,
    default: () => []
    },  

    selectedLocation: {
      type: String,
      required: true,
    },

    showMachineLoadModal: {
      type: Boolean,
      default: false
    },
    selectedMachineLoad: {
      type: Object,
      default: null
    },  
    lockedMachines: {
      type: Object,
      default: () => ({})
    },
    
  },

  mounted() {

    //console.log("📦 Received planningDays:", this.planningDays);
    this.localMachines.forEach(machine => {
      machine.orders.forEach(order => {
        console.log('Order loaded:', {
          orderId: order.orderId,
          ord_no: order.ord_no,
          partNumber: order.partNumber,
          ord_qty: order.ord_qty
        });
        this.initialOrdersState[order.partNumber] = {
          machine: machine.name,
        };
      });
    });
    console.log('Initial orders state captured:', this.initialOrdersState);
  },

  

  setup(props, { emit }) {  // <-- emit is part of the second argument of setup function
    //console.log('🚀 unscheduledForecasts in MachinesView:', props.unscheduledForecasts);

    const localMachines = ref([...props.machines]);
    const initialOrdersState = ref({});
    const modifiedOrders = ref({});
    const localResources = ref([...props.resources]); // Initialize resources as a reactive list

    const planningDateLabels = computed(() => {
      const labels = [];
      const cursor = new Date();

      // Noon avoids daylight-saving date shifts.
      cursor.setHours(12, 0, 0, 0);

      // Pablo schedules Monday through Saturday.
      // If today is Sunday, start with Monday.
      while (cursor.getDay() === 0) {
        cursor.setDate(cursor.getDate() + 1);
      }

      while (labels.length < Number(props.planningDays || 0)) {
        labels.push({
          dayNumber: labels.length + 1,
          dayName: cursor.toLocaleDateString('en-US', {
            weekday: 'short'
          }),
          dateLabel: `${cursor.getMonth() + 1}/${cursor.getDate()}`
        });

        // Advance to the next scheduling day, skipping Sunday.
        do {
          cursor.setDate(cursor.getDate() + 1);
        } while (cursor.getDay() === 0);
      }

      return labels;
    });

        // ⭐ Sales Demand modal state
    const showDayDemandModal = ref(false);
    const selectedDayDemand = ref(null);
    const sendingMoveOutReq = ref(false);
    const moveOutReqError = ref("");
    const moveOutReqSuccess = ref("");


    const onChangeMachineLimit = (e, machineName) => {
      const raw = e.target.value;
      const value = raw === '' ? null : Number(raw);

      // keep local state in sync (optional but recommended)
      machineItemLimitOverrides.value[machineName] = value;

      emit('update-machine-item-limit', { machineName, value });
    };

    // ✅ Production → Logistics: request move out for item + production day
    const emitMoveOutRequest = (itemNo, productionDay) => {
      emit("open-moveout-request", {
        item_no: String(itemNo || "").trim(),
        production_day: Number(productionDay)
      });
    };

    
    const machineItemLimitOverrides = ref({});

    //console.log('Received materialAlerts in MachinesView:', props.materialAlerts);

    const formatHours = (v) => {
      const n = Number(v ?? 0);
      return Number.isFinite(n) ? n.toFixed(2) : '0.00';
    };

    const formatPct = (v) => {
      const n = Number(v ?? 0);
      return Number.isFinite(n) ? n.toFixed(1) + '%' : '0.0%';
    };

    const formatQty = (v) => {
      const n = Number(v ?? 0);
      return Number.isFinite(n) ? Math.round(n).toString() : '0';
    };

    const getFgShortDisplay = (machine, order, day) => {
      if (!order || !day) return '';

      const current = Number(
        order[`fg_short_${day}`] ??
        order[`FG SHORT ${day}`] ??
        0
      ) || 0;

      if (current <= 0) return '';

      const previous =
        Number(day) === 1
          ? 0
          : Number(
              order[`fg_short_${Number(day) - 1}`] ??
              order[`FG SHORT ${Number(day) - 1}`] ??
              0
            ) || 0;

      // FG SHORT is cumulative.
      // Show the cumulative amount only on a day when
      // this WO's shortage increases.
      if (current > previous) {
        return Math.round(current).toString();
      }

      return '';
    };

    
    const isPendingOrder = (order) => {
      if (!order) return false;

      const source = String(order.source || '')
        .trim()
        .toUpperCase();

      const syncStatus = String(order.sync_status || '')
        .trim()
        .toUpperCase();

      return (source === 'PABLO' && syncStatus === 'PENDING') ||
        (!source && !syncStatus);
    };

    const onKeepManualSequenceChange = (
      event,
      order
    ) => {
      const keepManualSequence =
        Boolean(event.target.checked);

      const ordNo = Number(
        order?.orderId ??
        order?.ord_no ??
        order?.ordNo
      );

      if (!Number.isInteger(ordNo) || ordNo <= 0) {
        console.error(
          'Invalid WO for Keep Position:',
          order
        );
        return;
      }

      // Change the checkbox immediately in the GUI.
      order.keep_manual_sequence =
        keepManualSequence;

      // Parent JIMScheduling.vue will save it.
      emit('set-keep-manual-sequence', {
        ord_no: ordNo,
        keep_manual_sequence: keepManualSequence
      });
    };

    const onWoExecutionFlagChange = (
      event,
      order,
      fieldName
    ) => {
      const ordNo = Number(
        order?.orderId ??
        order?.ord_no ??
        order?.ordNo
      );

      if (!Number.isInteger(ordNo) || ordNo <= 0) {
        console.error(
          'Invalid WO for execution flag:',
          order
        );
        return;
      }

      const checked = Boolean(event.target.checked);

      // Immediate GUI update
      order[fieldName] = checked;

      emit('set-work-order-execution-flags', {
        ord_no: ordNo,

        shift_changeover_1st:
          Boolean(order.shift_changeover_1st),

        shift_changeover_2nd:
          Boolean(order.shift_changeover_2nd),

        exact_quantity:
          Boolean(order.exact_quantity)
      });
    };

    const getRunQty = (order, day) => {
      return Number(
        order?.[`RUN ${day}`] ??
        order?.[`run_${day}`] ??
        0
      ) || 0;
    };

    const getFgShortQtyForDisplayedBox = (machine, order, day) => {
      if (!order || !day) return 0;

      const current = Number(
        order[`fg_short_${day}`] ??
        order[`FG SHORT ${day}`] ??
        0
      ) || 0;

      if (current <= 0) return 0;

      const previous =
        Number(day) === 1
          ? 0
          : Number(
              order[`fg_short_${Number(day) - 1}`] ??
              order[`FG SHORT ${Number(day) - 1}`] ??
              0
            ) || 0;

      // Match the visible display rule.
      if (current > previous) {
        return current;
      }

      return 0;
    };

        const getMachineScheduledQtyTotal = (machine, day) => {
      return (machine?.orders || []).reduce(
        (total, order) => total + getRunQty(order, day),
        0
      );
    };

    const getMachineShortQtyTotal = (machine, day) => {
      return (machine?.orders || []).reduce(
        (total, order) =>
          total + getFgShortQtyForDisplayedBox(machine, order, day),
        0
      );
    };

const showMachineHoursModal = ref(false);
const selectedMachineHours = ref(null);

const machineHoursLoading = ref(false);
const machineHoursSaving = ref(false);

const machineCapacityLoading = ref(false);
const machineCapacitySaving = ref(false);
const machineCapacityRows = ref([]);

const machineHoursError = ref("");
const machineHoursSuccess = ref("");


const getCleanLoc = () => {
  return String(
    props.selectedLocation || ""
  )
    .trim()
    .toUpperCase()
    .slice(0, 1);
};


const formatCapacityDate = (value) => {
  if (!value) return "";

  const raw = String(value)
    .trim()
    .slice(0, 10);

  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})$/
  );

  if (!match) {
    return raw;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  // Noon prevents DST/browser timezone edge cases.
  const date = new Date(
    year,
    month - 1,
    day,
    12,
    0,
    0,
    0
  );

  const weekday = date.toLocaleDateString(
    "en-US",
    {
      weekday: "short"
    }
  );

  return `${weekday} ${month}/${day}`;
};


const loadMachineCapacityRows = async () => {
  if (!selectedMachineHours.value) {
    machineCapacityRows.value = [];
    return;
  }

  const machine = String(
    selectedMachineHours.value.machine || ""
  ).trim();

  const loc = String(
    selectedMachineHours.value.loc || ""
  )
    .trim()
    .toUpperCase()
    .slice(0, 1);

  if (!machine || !loc) {
    machineCapacityRows.value = [];
    return;
  }

  machineCapacityLoading.value = true;

  try {
    const response = await axios.get(
      "/machine-capacity-calendar",
      {
        params: {
          machine,
          loc
        }
      }
    );

    const rawRows = Array.isArray(
      response.data
    )
      ? response.data
      : (
          Array.isArray(response.data?.rows)
            ? response.data.rows
            : []
        );

    machineCapacityRows.value = rawRows
      .map(row => {
        const defaultHours = Number(
          row.default_allowed_hours ?? 0
        );

        const effectiveHours = Number(
          row.allowed_hours ??
          defaultHours
        );

        return {
          ...row,

          machine: String(
            row.machine || machine
          ).trim(),

          loc: String(
            row.loc || loc
          )
            .trim()
            .toUpperCase(),

          work_date: String(
            row.work_date || ""
          )
            .trim()
            .slice(0, 10),

          day_num: Number(
            row.day_num
          ),

          default_allowed_hours:
            Number.isFinite(defaultHours)
              ? defaultHours
              : 0,

          override_allowed_hours:
            row.override_allowed_hours === null ||
            row.override_allowed_hours === undefined
              ? null
              : Number(
                  row.override_allowed_hours
                ),

          allowed_hours:
            Number.isFinite(effectiveHours)
              ? effectiveHours
              : 0,

          is_override:
            Boolean(row.is_override),

          // Editable value starts at EFFECTIVE capacity.
          edit_hours:
            Number.isFinite(effectiveHours)
              ? effectiveHours
              : 0,

          saving: false
        };
      })
      .sort(
        (a, b) =>
          Number(a.day_num) -
          Number(b.day_num)
      );

  } catch (err) {
    console.error(
      "loadMachineCapacityRows error:",
      err
    );

    machineCapacityRows.value = [];

    machineHoursError.value =
      err?.response?.data?.error ||
      "Failed to load daily machine capacity.";

  } finally {
    machineCapacityLoading.value = false;
  }
};


const openMachineHoursMaintenance = async (
  machine
) => {
  machineHoursError.value = "";
  machineHoursSuccess.value = "";

  machineHoursLoading.value = true;
  machineCapacityLoading.value = true;

  const loc = getCleanLoc();

  const machineName = String(
    machine?.name ??
    machine?.machineName ??
    machine?.machine ??
    ""
  ).trim();

  selectedMachineHours.value = {
    machine: machineName,
    loc,
    allowed_hours: 18
  };

  machineCapacityRows.value = [];

  showMachineHoursModal.value = true;

  try {
    const response = await axios.get(
      "/machine-hours",
      {
        params: {
          machine: machineName,
          loc
        }
      }
    );

    selectedMachineHours.value = {
      machine: String(
        response.data.machine ??
        machineName
      ).trim(),

      loc: String(
        response.data.loc ??
        loc
      )
        .trim()
        .toUpperCase(),

      allowed_hours: Number(
        response.data.allowed_hours ?? 18
      )
    };

  } catch (err) {
    console.error(
      "openMachineHoursMaintenance error:",
      err
    );

    machineHoursError.value =
      err?.response?.data?.error ||
      "Failed to load normal machine hours.";

  } finally {
    machineHoursLoading.value = false;
  }

  // Daily capacity is loaded separately so a failure
  // here does not prevent normal-hours maintenance.
  await loadMachineCapacityRows();
};


const closeMachineHoursMaintenance = () => {
  showMachineHoursModal.value = false;

  selectedMachineHours.value = null;
  machineCapacityRows.value = [];

  machineHoursError.value = "";
  machineHoursSuccess.value = "";

  machineHoursLoading.value = false;
  machineHoursSaving.value = false;

  machineCapacityLoading.value = false;
  machineCapacitySaving.value = false;
};


const saveMachineHours = async () => {
  machineHoursError.value = "";
  machineHoursSuccess.value = "";

  if (!selectedMachineHours.value) {
    return;
  }

  const allowedHours = Number(
    selectedMachineHours.value.allowed_hours
  );

  if (
    !Number.isFinite(allowedHours) ||
    allowedHours < 0 ||
    allowedHours > 24
  ) {
    machineHoursError.value =
      "Normal hours must be between 0 and 24.";

    return;
  }

  try {
    machineHoursSaving.value = true;

    await axios.put(
      "/machine-hours",
      {
        machine:
          selectedMachineHours.value.machine,

        loc:
          selectedMachineHours.value.loc,

        allowed_hours:
          allowedHours
      }
    );

    machineHoursSuccess.value =
      "✅ Normal machine hours saved.";

    // Important:
    // changing normal hours changes every date
    // that does NOT have an override.
    await loadMachineCapacityRows();

    emit(
      "machine-hours-updated",
      {
        machine:
          selectedMachineHours.value.machine,

        loc:
          selectedMachineHours.value.loc,

        allowed_hours:
          allowedHours
      }
    );

  } catch (err) {
    console.error(
      "saveMachineHours error:",
      err
    );

    machineHoursError.value =
      err?.response?.data?.error ||
      "Failed to save normal machine hours.";

  } finally {
    machineHoursSaving.value = false;
  }
};


const saveMachineCapacityOverride = async (
  row
) => {
  machineHoursError.value = "";
  machineHoursSuccess.value = "";

  if (!row || !selectedMachineHours.value) {
    return;
  }

  const hours = Number(
    row.edit_hours
  );

  if (
    !Number.isFinite(hours) ||
    hours < 0 ||
    hours > 24
  ) {
    machineHoursError.value =
      "Daily hours must be between 0 and 24.";

    return;
  }

  const normalHours = Number(
    row.default_allowed_hours
  );

  // If the planner enters the normal/default value,
  // an override is unnecessary.
  //
  // If one currently exists, resetting it is the
  // cleanest representation.
  if (
    Number.isFinite(normalHours) &&
    hours === normalHours
  ) {
    if (row.is_override) {
      await resetMachineCapacityOverride(
        row
      );
    } else {
      machineHoursSuccess.value =
        `${formatCapacityDate(
          row.work_date
        )} is already using normal capacity.`;
    }

    return;
  }

  row.saving = true;
  machineCapacitySaving.value = true;

  try {
    await axios.put(
      "/machine-capacity-override",
      {
        machine:
          selectedMachineHours.value.machine,

        loc:
          selectedMachineHours.value.loc,

        work_date:
          row.work_date,

        allowed_hours:
          hours
      }
    );

    machineHoursSuccess.value =
      `✅ ${formatCapacityDate(
        row.work_date
      )} capacity saved.`;

    await loadMachineCapacityRows();

    emit(
      "machine-hours-updated",
      {
        machine:
          selectedMachineHours.value.machine,

        loc:
          selectedMachineHours.value.loc,

        work_date:
          row.work_date,

        allowed_hours:
          hours,

        capacity_override: true
      }
    );

  } catch (err) {
    console.error(
      "saveMachineCapacityOverride error:",
      err
    );

    machineHoursError.value =
      err?.response?.data?.error ||
      "Failed to save daily capacity.";

  } finally {
    row.saving = false;
    machineCapacitySaving.value = false;
  }
};


const resetMachineCapacityOverride = async (row) => {
  try {
    const loc = String(
      row?.loc ||
      selectedMachineHours.value?.loc ||
      getCleanLoc() ||
      ''
    )
      .trim()
      .toUpperCase()
      .slice(0, 1);

    const machine = String(
      row?.machine ||
      selectedMachineHours.value?.machine ||
      ''
    ).trim();

    const workDate = String(
      row?.work_date || ''
    )
      .trim()
      .slice(0, 10);

    if (!['F', 'D', 'K', 'L'].includes(loc)) {
      throw new Error(
        `Invalid location for reset: ${loc}`
      );
    }

    if (!machine) {
      throw new Error(
        'Machine is missing for capacity reset.'
      );
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(workDate)) {
      throw new Error(
        `Invalid work_date for reset: ${workDate}`
      );
    }

    console.log(
      '🧪 Reset capacity override request:',
      {
        loc,
        machine,
        work_date: workDate
      }
    );

    await axios.delete(
      '/machine-capacity-override',
      {
        params: {
          loc,
          location: loc,
          machine,
          work_date: workDate
        }
      }
    );

    await loadMachineCapacityRows();

  } catch (err) {
    console.error(
      'resetMachineCapacityOverride error:',
      {
        status: err?.response?.status,
        data: err?.response?.data,
        params: err?.config?.params,
        message: err?.message
      }
    );
  }
};

const shortageIndicatorLabels = {
  I: 'Glass shortage',
  D: 'Duct guard shortage',
  B: 'Bag shortage',
  Bx: 'Carton shortage',
  S: 'Scrim shortage',
  C: 'Core shortage',
  J: 'Jacket shortage',
  '?': 'Unmapped material shortage'
};

const shortageIndicatorOrder = {
  I: 1,
  D: 2,
  B: 3,
  Bx: 4,
  S: 5,
  C: 6,
  J: 7,
  '?': 99
};

const getShortageIndicators = (order) => {
  if (!order) return [];

  const rawValue =
    order.shortageIndicators ??
    order.shortage_indicators ??
    '';

  let indicators = [];

  if (Array.isArray(rawValue)) {
    indicators = rawValue;
  } else {
    indicators = String(rawValue)
      .split(',');
  }

  return [
    ...new Set(
      indicators
        .map(value => String(value || '').trim())
        .filter(Boolean)
    )
  ].sort(
    (a, b) =>
      (shortageIndicatorOrder[a] ?? 98) -
      (shortageIndicatorOrder[b] ?? 98)
  );
};

const getShortageIndicatorTitle = (indicator) => {
  return shortageIndicatorLabels[indicator]
    || 'Material shortage';
};

const getScheduledRunQtyForOrderBeforeDay = (order, day) => {
  if (!order || !day) return 0;

  const currentDay = Number(day) || 0;

  const ordQty = Math.max(
    0,
    Number(order.ord_qty ?? 0) || 0
  );

  let total = 0;

  // Only production scheduled BEFORE the shortage day counts.
  //
  // Important:
  // RUN can represent the same WO on more than one day because
  // RUN = max(D, R). Therefore we cap accumulated RUN at the
  // actual remaining WO quantity.
  for (let d = 1; d < currentDay; d++) {
    const runQty = Math.max(
      0,
      getRunQty(order, d)
    );

    const remainingWoCapacity = Math.max(
      0,
      ordQty - total
    );

    if (remainingWoCapacity <= 0) break;

    total += Math.min(
      runQty,
      remainingWoCapacity
    );
  }

  return total;
};


const getFgShortCoverageStatus = (machine, order, day) => {
  if (!order || !day) return '';

  const currentShortage = Math.max(
    0,
    Number(
      order[`fg_short_${day}`] ??
      order[`FG SHORT ${day}`] ??
      0
    ) || 0
  );

  // No shortage on this WO by this day.
  if (currentShortage <= 0) return '';

  // Only color the day where the cumulative shortage
  // actually increases.
  const previousShortage =
    Number(day) === 1
      ? 0
      : Math.max(
          0,
          Number(
            order[`fg_short_${Number(day) - 1}`] ??
            order[`FG SHORT ${Number(day) - 1}`] ??
            0
          ) || 0
        );

  if (currentShortage <= previousShortage) return '';

  const scheduledBeforeDay =
    getScheduledRunQtyForOrderBeforeDay(
      order,
      day
    );

  // FG SHORT is cumulative at the WO level, so compare
  // prior scheduled production directly to the current
  // cumulative shortage quantity.
  return scheduledBeforeDay >= currentShortage
    ? 'covered'
    : 'uncovered';
};    

    const getDayBoxClass = (machine, order, day) => {
      const coverageStatus = getFgShortCoverageStatus(machine, order, day);

      return {
        'has-qty': Number(order?.[`RUN ${day}`] || 0) > 0,
        'fg-short-covered': coverageStatus === 'covered',
        'fg-short-uncovered': coverageStatus === 'uncovered'
      };
    };

    
    watch(
      () => props.machines,
      (newMachines) => {
        localMachines.value = [...newMachines];
      }
    );

    const checkOrdQty = (ord_qty) => {
      //console.log('ord_qty value:', ord_qty, 'Type:', typeof ord_qty);
      return ord_qty;
    };

    const hasUnscheduledForecast = (workOrder) => {
      if (!props.unscheduledForecasts) return false;
      return props.unscheduledForecasts.some(
        forecast => forecast.item_no.trim() === workOrder.partNumber.trim()
      );
    };

    const formatDate = (d) => {
      if (!d) return '';

      const s = String(d).trim();

      // YYYY-MM-DD → treat as local date (NO timezone shift)
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) {
        return `${m[2]}/${m[3]}/${m[1]}`;
      }

      // fallback for true timestamps / Date objects
      const dt = d instanceof Date ? d : new Date(d);
      if (Number.isNaN(dt.getTime())) return s;
      return dt.toLocaleDateString();
    };

    const canMoveOrder = (evt) => {
      const order = evt?.draggedContext?.element;
      if (!order) return true;
      return !order.locked;  // ✅ locked orders cannot move
    };




    const hasInsufficiency = (workOrder) => {
  if (!props.materialAlerts || props.materialAlerts.length === 0) return false;

  const itemNo = workOrder.partNumber; // this should be the FG item on the WO row
  const result = props.materialAlerts.some(alert => alert.item_no === itemNo);

  console.log(`Checking insufficiency for ${itemNo}:`, result, {
    itemNo,
    alerts: props.materialAlerts
  });

  return result;
};

const getInsufficiency = (workOrder) => {
  if (!props.materialAlerts || props.materialAlerts.length === 0) return '';

  const itemNo = workOrder.partNumber;
  const alert = props.materialAlerts.find(alert => alert.item_no === itemNo);

  console.log(`Insufficiency for ${itemNo}:`, alert ? alert.insufficiency : 'None', {
    itemNo,
    alert
  });

  // If you want “Y”/blank:
  // return alert ? 'Y' : '';

  // If you want the numeric shortage value:
  return alert ? alert.insufficiency : '';
};



const requestDayDemand = (order, dayIndex) => {
  emit('show-day-demand', { order, dayIndex });
};

// ⭐ Close modal
const closeDayDemandModal = () => {
  showDayDemandModal.value = false;
  selectedDayDemand.value = null;
};

const requestLogisticsMoveOut = async (row) => {
  try {
    moveOutReqError.value = "";
    moveOutReqSuccess.value = "";
    sendingMoveOutReq.value = true;

    if (!selectedDayDemand.value?.order?.partNumber) {
      moveOutReqError.value = "Missing item number.";
      return;
    }

    const itemNo = String(selectedDayDemand.value.order.partNumber || "").trim();
    const productionDay = Number(selectedDayDemand.value.dayIndex);
    const ordNo = String(row?.ord_no || "").trim();

    if (!ordNo) {
      moveOutReqError.value = "Missing sales order number.";
      return;
    }

    // ✅ Hardcoded reason for now (we’ll add dropdown next step)
    const reasonCode = "CAPACITY";

    const loc = String(props.selectedLocation || '')
      .trim()
      .toUpperCase()
      .slice(0, 1);


    const payload = {
      loc,
      sender_dept: "Production",
      recipient_dept: "Logistics",
      request_type: "PROD_REQUEST_MOVE_OUT",
      item_no: itemNo,
      payload: {
        item_no: itemNo,
        production_day: productionDay,
        req_ship_dt: row.req_ship_dt || null,
        order_nos: [ordNo],
        qty_ordered: Number(row.qty_ordered) || 0,
        reason_code: reasonCode
      },
      requires_replan: false
    };

    await axios.post("/pablotify/request", payload);

    moveOutReqSuccess.value = `✅ Request sent for SO ${ordNo}`;

  } catch (err) {
    console.error("requestLogisticsMoveOut error:", err);
    moveOutReqError.value = "Failed to send request.";
  } finally {
    sendingMoveOutReq.value = false;
  }
};




    
    // Existing onDragStart method for work orders
    const onDragStart = (event) => {
      const item = event.item;
      const ord_no = parseInt(item.dataset.ordNo, 10); // Convert ord_no to an integer

      console.log('Order details on drag start:', {
        ord_no,
        partNumber: item.dataset.partNumber,
        ord_qty: item.dataset.ordQty,
      });

      if (isNaN(ord_no)) {
        console.error('Invalid work order number on drag start:', ord_no);
      } else {
        console.log(`Drag started for order ${ord_no}`);
        item.dataset.ordNo = ord_no; // Ensure ordNo is stored as an integer
      }
    };

    const onDragEnd = async (event) => {
      const item = event?.item;

      const ord_no = parseInt(
        item?.dataset?.ordNo,
        10
      );

      if (Number.isNaN(ord_no)) {
        console.error('Invalid work order number after drag:', ord_no);
        emit('schedule-resequence-failed');
        return;
      }

      const fromRow = event.from?.closest?.('.machine-row');
      const toRow = event.to?.closest?.('.machine-row');

      const fromMachine = String(
        fromRow?.querySelector?.('.machine-name')?.innerText || ''
      ).trim();

      const toMachine = String(
        toRow?.querySelector?.('.machine-name')?.innerText || ''
      ).trim();

      if (!toMachine) {
        console.error('No destination machine was found.');
        emit('schedule-resequence-failed');
        return;
      }

      // UnscheduledView creates the Pending work order.
      // Reconciliation will append that new Pending order when the schedule reloads.
      const cameFromUnscheduled = Boolean(
        event.from?.closest?.('.unscheduled')
      );

      if (cameFromUnscheduled) {
        console.log(
          'Drop came from Unscheduled. Waiting for Pending WO creation and schedule refresh.'
        );
        return;
      }

      if (!fromMachine) {
        console.error('No source machine was found.');
        emit('schedule-resequence-failed');
        return;
      }

      const loc = String(props.selectedLocation || '')
        .trim()
        .toUpperCase()
        .slice(0, 1);

      if (!['F', 'D', 'K', 'L'].includes(loc)) {
        console.error('Invalid schedule location:', loc);
        emit('schedule-resequence-failed');
        return;
      }

      const machineNames = Array.from(
        new Set([fromMachine, toMachine])
      );

      const machines = {};

      for (const machineName of machineNames) {
        const machine = localMachines.value.find(
          row => String(row?.name || '').trim() === machineName
        );

        if (!machine) {
          console.error(
            `Could not find local machine state for ${machineName}`
          );
          emit('schedule-resequence-failed');
          return;
        }

        machines[machineName] = (machine.orders || [])
          .map(order =>
            Number(
              order?.orderId ??
              order?.ord_no ??
              order?.ordNo
            )
          )
          .filter(orderNumber =>
            Number.isInteger(orderNumber) &&
            orderNumber > 0
          );
      }

      const payload = {
        loc,
        moved_ord_no: ord_no,
        from_machine: fromMachine,
        to_machine: toMachine,
        machines
      };

      try {
        const response = await axios.post(
          '/schedule/resequence',
          payload
        );

        console.log(
          'Schedule sequence saved:',
          response.data
        );

        emit('schedule-resequenced', response.data);
      } catch (error) {
        console.error(
          'Could not save schedule sequence:',
          error?.response?.data || error.message
        );

        alert(
          error?.response?.data?.message ||
          'The schedule could not be saved. The schedule will be reloaded.'
        );

        // Parent reloads the authoritative database state.
        emit('schedule-resequence-failed', {
          ord_no,
          from_machine: fromMachine,
          to_machine: toMachine
        });
      }
    };

    const machinesScrollEl = ref(null);

    const onMachinesDragOver = (e) => {
      const el = machinesScrollEl.value;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const y = e.clientY - rect.top;       // cursor position inside container
      const edge = 70;                      // px from top/bottom where scroll starts
      const speed = 18;                     // px per event (tune)

      if (y < edge) {
        el.scrollTop -= speed;
      } else if (y > rect.height - edge) {
        el.scrollTop += speed;
      }
    };

    const onRunDragStart = (e, order, fromDay) => {
  // Only drag if there is something to move
      const qty = Number(order[`RUN ${fromDay}`] || 0);
      if (!qty || qty <= 0) {
        e.preventDefault();
        return;
      }

      const payload = {
        ord_no: order.orderId,
        item_no: order.partNumber,
        from_day: fromDay,
        qty
      };

      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('application/json', JSON.stringify(payload));
      e.dataTransfer.setData('text/plain', JSON.stringify(payload)); // fallback
    };

    const onRunDrop = (e, order, toDay) => {
      let raw = e.dataTransfer.getData('application/json') || e.dataTransfer.getData('text/plain');
      if (!raw) return;

      let payload;
      try {
        payload = JSON.parse(raw);
      } catch {
        return;
      }

      // Only allow within same work order card
      if (!payload || payload.ord_no !== order.orderId) return;

      const fromDay = Number(payload.from_day);
      if (!Number.isFinite(fromDay) || fromDay === toDay) return;

      emit('run-plan-move', {
        location: props.selectedLocation,
        ord_no: order.orderId,
        item_no: order.partNumber,
        from_day: fromDay,
        to_day: toDay,
        mode: 'merge'
      });
    };

    const onRunEditClick = (order, day) => {
      const current = Number(order[`RUN ${day}`] || 0);

      const input = window.prompt(
        `Set run quantity for D ${day}:`,
        current.toFixed(2)
      );

      if (input === null) return; // user cancelled

      const qty = Number(input);
      if (!Number.isFinite(qty) || qty < 0) {
        alert('Please enter a valid non-negative number.');
        return;
      }

      emit('run-plan-set', {
        location: props.selectedLocation,
        ord_no: order.orderId,
        item_no: order.partNumber,
        day_num: day,
        run_qty_override: qty
      });
    };




    const onDropEmployee = (event, machine, role) => {
      const employeeId = event.dataTransfer.getData("employeeId");
      const employeeName = event.dataTransfer.getData("employeeName");

      console.log(`Employee dropped: ${employeeName} as ${role} onto machine ${machine.name}`);

      if (role === 'operator') {
        machine.operator = { employee_id: employeeId, employee_name: employeeName };
      } else if (role === 'boxer') {
        machine.boxer = { employee_id: employeeId, employee_name: employeeName };
      }

      // Use emit function to mark the employee as assigned
      emit('mark-resource-dropped', employeeId);  // <-- Correct way to emit the event
    };

    const allowDrop = (event) => {
      event.preventDefault(); 
      console.log('Employee is being dragged over drop zone');
    };

        // --- NEW: invalid drop state ---
    const invalidDrop = ref(false);
    const invalidDropPosition = ref({ x: 0, y: 0 });
    let lastMousePosition = { x: 0, y: 0 };

    const onMouseMove = (e) => {
      lastMousePosition = { x: e.clientX, y: e.clientY };
    };

    onMounted(() => {
      window.addEventListener('mousemove', onMouseMove);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('mousemove', onMouseMove);
    });

    // --- NEW: function to show Ghostbusters overlay ---
    const showInvalidDropEffect = () => {
      invalidDropPosition.value = { ...lastMousePosition };
      invalidDrop.value = true;
      setTimeout(() => {
        invalidDrop.value = false;
      }, 500); // 0.5 seconds – tweak to taste
    };
    


    const canDrop = (machineName) => {
      return (to, from, item) => {
        const partNumber = item.dataset.partNumber;
        if (!props.throughput || !partNumber) {
          console.log(`Dropping ${partNumber} into ${machineName} is not allowed (no throughput data).`);
          showInvalidDropEffect();
          return false;
        }

        const cleanMachineName = machineName.replace(/^Machine\s+/, '');

        const machineThroughput = props.throughput[cleanMachineName];
        if (!machineThroughput) {
          console.log(`No throughput data for machine ${cleanMachineName}.`);
          showInvalidDropEffect();
          return false;
        }

        const throughput = machineThroughput[partNumber];
        if (throughput === undefined) {
          console.log(`No throughput data for part number ${partNumber} on machine ${cleanMachineName}.`);
          showInvalidDropEffect();
          return false;
        }

        const canDrop = throughput > 0;
        console.log(`Checking if ${partNumber} can be dropped into ${cleanMachineName}:`, canDrop);
        
        if (!canDrop) {
          showInvalidDropEffect(); // << NEW (rejecting HERE too)
        }
        
        return canDrop;
      };
    };

    const getMachineItemClass = (order) => {
      const classes = [];

      const itemNo = String(
        order?.partNumber ??
        order?.item_no ??
        ''
      )
        .trim()
        .toUpperCase();

      const productType = String(
        order?.product_type ??
        order?.type ??
        ''
      )
        .trim()
        .toUpperCase();

      let rValue = '';

      const rawRValue =
        order?.r_value ??
        order?.rValue ??
        '';

      const numericRValue = Number(rawRValue);

      if (Number.isFinite(numericRValue)) {
        if (numericRValue === 4.2) {
          rValue = '4.2';
        } else if (numericRValue === 6) {
          rValue = '6';
        } else if (numericRValue === 8) {
          rValue = '8';
        }
      }

      // Fallback when r_value is unavailable.
      if (!rValue) {
        if (
          itemNo.includes('R4.2') ||
          itemNo.includes('R42')
        ) {
          rValue = '4.2';
        } else if (itemNo.includes('R6')) {
          rValue = '6';
        } else if (itemNo.includes('R8')) {
          rValue = '8';
        }
      }

      // Mobile Home takes priority over R-value coloring.
      if (productType === 'MH25') {
        classes.push('item-mobile-home');
      } else if (rValue === '4.2') {
        classes.push('item-r42');
      } else if (rValue === '6') {
        classes.push('item-r6');
      } else if (rValue === '8') {
        classes.push('item-r8');
      }

      // Item numbers without B are carton products.
      if (itemNo && !itemNo.includes('B')) {
        classes.push('item-carton');
      }

      return classes;
    };

    return {
      localMachines,
      onDragStart,
      onDragEnd,
      allowDrop, // Add this to return the employee drag-drop handler
      onDropEmployee, // Add this to return the employee drop handler
      canDrop,
      hasInsufficiency,
      getInsufficiency,
      checkOrdQty,
      initialOrdersState,
      modifiedOrders,
      localResources, // Return localResources for use in other methods
      hasUnscheduledForecast, // 👈 Don't forget this
      invalidDrop,
      invalidDropPosition,
      onMachinesDragOver,
      machinesScrollEl,
      showDayDemandModal,
      selectedDayDemand,
      closeDayDemandModal,
      formatDate,
      requestDayDemand,
      onChangeMachineLimit,
      machineItemLimitOverrides,
      formatHours,
      formatPct,
      formatQty,
      onRunDragStart,
      onRunDrop,
      onRunEditClick,
      emitMoveOutRequest,
      requestLogisticsMoveOut,
      sendingMoveOutReq,
      moveOutReqError,
      moveOutReqSuccess,
      canMoveOrder,
      getFgShortDisplay,
      getDayBoxClass,
      getFgShortCoverageStatus,
      getMachineScheduledQtyTotal,
      getMachineShortQtyTotal,
      getShortageIndicators,
      getShortageIndicatorTitle,

      showMachineHoursModal,
      selectedMachineHours,

      machineHoursLoading,
      machineHoursSaving,

      machineCapacityLoading,
      machineCapacitySaving,
      machineCapacityRows,

      machineHoursError,
      machineHoursSuccess,

      openMachineHoursMaintenance,
      closeMachineHoursMaintenance,
      saveMachineHours,

      loadMachineCapacityRows,
      saveMachineCapacityOverride,
      resetMachineCapacityOverride,
      formatCapacityDate,

      isPendingOrder,

      planningDateLabels,

      getMachineItemClass,
      onKeepManualSequenceChange,

      onWoExecutionFlagChange,
              
    };
  },
};
</script>

<style scoped>
.scrollable-machines-container {
  max-height: 80vh; /* Adjust this height as needed to fit your layout */
  overflow-y: auto; /* Enables vertical scrolling for all machine boxes */
  width: 100%; /* Adjust this calculation based on the width of the Unscheduled Production box */
  margin-right: 0px; /* Adds some space between the machine container and the Unscheduled Production box */
  border-right: 1px solid #000; /* Ensures the right border is visible */
  box-sizing: border-box; /* Includes the border in the width calculation */
  border-radius: 10px; /* Adds rounded corners */
  background-color: #0b0101; /* Light Salmon background color for day boxes */
  padding-right: 10px;
  overflow-y: auto;
  max-height: 80vh;
}

.machine-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 10px;
  border: 1px solid #ccc; /* Border color */
  padding: 10px;
  width: 100%; /* Set this to fill the container */
  box-sizing: border-box; /* Ensures the border is included in the width calculation */
  border-radius: 10px; /* Adds rounded corners */
  background-color: #708090a4 !important; /* White background, ensures no inheritance issue */
  color: #333; /* Dark gray text color */
  /*border-color: #ccc; /* Light gray border color */
  margin-left: 10px; /* Adds some space between the machine container and the Unscheduled Production box */
}

/* Optional: Add hover effect for machine-row */
.machine-row:hover {
  background-color: #3b3a48; /* Slightly darker gray on hover */
  border-color: #999; /* Darker gray border on hover */
}

.machine-header {
  display: flex;
  flex-direction: column;

  width: 20%;
  flex: 0 0 20%;
  max-width: 20%;
  box-sizing: border-box;
}

.machine-name {
  font-weight: bold;
  margin-bottom: 5px;
  color: #cbbfbf; /* Adjust the color if needed */
}

.machine-comments {
  width: 50%;
  margin-bottom: 10px;
}

.machine-orders {
  flex: 2;
  display: flex;
  margin-right: 10px;
  margin-left: 2px;
  width: 100%;          /* make the orders area fill the row */
  box-sizing: border-box;
}

.order-info {
  display: flex;
  align-items: center;
  margin-left: 10px;
  flex: 1 1 auto;       /* let it grow to fill available space */
  width: 100%;          /* ensure it can span full width of machine-orders */
}

.order-details {
  display: flex;
  flex-direction: column;
  margin-right: 20px;

  width: 150px;
  min-width: 150px;
  flex: 0 0 150px;
}

.day-boxes {
  display: flex;
  flex-direction: row;
  flex: 0 0 auto;
}

.day-box {
  position: relative;
  width: 50px;
  height: 50px;
  flex-shrink: 0;
  display: flex;
  flex-direction: row;        /* ✅ force single row */
  justify-content: flex-start;/* ✅ don't center stack */
  align-items: center;
  gap: 6px;                   /* ✅ spacing between label/value/badge */
  padding: 0 18px;             /* ✅ room for MUST */
  border: 1px solid #ccc;
  margin-right: 5px;
  border-radius: 5px;
  background-color: #8fbcd193;
  color: #000000;
  white-space: nowrap;        /* ✅ prevent D / value wrapping */
  overflow: hidden;           /* ✅ keep box tidy */
}

/* Add hover effect for day-boxes */
.day-box:hover {
  background-color: #5595b7c2; /* Grey color on hover */
  border-color: #999; /* Darker border color on hover */
}

.day-box.has-qty {
  color: white;         /* 🔥 WHITE TEXT WHEN >0 */
  font-weight: 600;     /* optional: bold */
  background-color: #2a74ff; /* optional: blue highlight, your call */
}

/* Finished-good shortage NOT covered by scheduled RUN qty */
.day-box.fg-short-uncovered {
  background-color: #d22c19;
  color: #fff;
  font-weight: 700;
}

/* Finished-good shortage covered by scheduled RUN qty */
.day-box.fg-short-covered {
  background-color: #f5c542;
  color: #111;
  font-weight: 700;
}

.day-box.fg-short-uncovered .day-label,
.day-box.fg-short-uncovered .day-value {
  color: #ffffff;
}

.day-box.fg-short-covered .day-label,
.day-box.fg-short-covered .day-value {
  color: #111;
}

.day-box.has-demand:not(.must-run) {
  background-color: #e3f2fd;
  color: #0d47a1;
}

.fg-short-corner {
  position: absolute;
  right: 4px;
  bottom: 3px;
  font-size: 0.68rem;
  font-weight: 800;
  line-height: 1;
  color: #fff;
  background: rgba(0, 0, 0, 0.32);
  border-radius: 6px;
  padding: 1px 4px;
  pointer-events: none;
}

.day-label,
.day-value {
  display: inline-block;
  line-height: 1;
}

.must-badge {
  margin-left: auto;          /* ✅ pushes MUST to the right */
  font-weight: 700;
  font-size: 0.72rem;
  padding: 2px 6px;
  border-radius: 8px;
  white-space: nowrap;
}

/* Standalone date row that mirrors a real machine/work-order row */
/*
  Shared wrapper for both:
  1. Real draggable work orders
  2. Display-only J 0 calendar row
*/
.machine-order-list {
  flex: 1 1 auto;
  min-width: 0;
}

/* J 0 is visual only */
.date-machine-row {
  position: sticky;
  top: 0;
  z-index: 50;

  min-height: 72px;
  background-color: #0b0101 !important;
  border-color: #555;
}

.date-machine-row:hover {
  background-color: #0b0101 !important;
  border-color: #555;
}

/* Remove the normal work-order appearance */
.machine-row .date-work-order {
  background-color: transparent;
  border-color: transparent;
  cursor: default;
  margin-bottom: 0;
}

.machine-row .date-work-order:hover {
  background-color: transparent;
  border-color: transparent;
}

/*
  Keep this empty, but preserve the exact 150px width
  and 20px right margin of a real order-details section.
*/
.date-order-placeholder {
  min-height: 1px;
}

/*
  Still uses .day-box, so width, padding, border,
  and spacing exactly match the real R boxes.
*/
.date-day-box {
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0;

  background-color: #1f2933;
  color: #ffffff;

  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1.1;

  cursor: default;
  pointer-events: none;
}

.date-day-box:hover {
  background-color: #1f2933;
  border-color: #ccc;
}

.planning-date-day {
  color: #f5c542;
}

.planning-date-value {
  margin-top: 3px;
  color: #ffffff;
}

.material-alerts,
.additional-info {
  width: 150px;
  margin-right: 2px;
  margin-left: 30px;
}

.machine-row .work-order {
  width: 100%;
  border: 1px solid #ccc;
  padding: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  margin-bottom: 5px;
  font-size: 12px;
  margin-left: 20px;
  margin-right: 10px;
  border-radius: 10px; /* Adds rounded corners */
  background-color: #5595b7c2; /* Lime Green background for individual work orders */
  color: #fff; /* Black text color for work orders */
  border-color: #006400; /* Dark Green border color */
}

/* Optional: Add hover effect for work orders */
.machine-row .work-order:hover {
  background-color: #999; /* Forest Green on hover */
  color: #fff; /* White text on hover */
}

.machine-row .work-order.unreviewed-work-order:not(.pending-work-order) {
  background-color: #c96f18;
  border: 2px solid #ffb45c;
  box-shadow: inset 5px 0 0 #ffd08a;
}

.machine-row .work-order.unreviewed-work-order:not(.pending-work-order):hover {
  background-color: #dc7d1f;
  border-color: #ffd08a;
}

.machine-row .work-order.pending-work-order {
  background-color: #4c3f67;
  border: 2px dashed #d8b4fe;
  box-shadow: inset 5px 0 0 #c084fc;
}

.machine-row .work-order.pending-work-order:hover {
  background-color: #5b4b78;
  border-color: #e9d5ff;
}

.pending-order-badge {
  display: inline-block;
  width: fit-content;
  margin-top: 4px;
  padding: 2px 6px;
  border: 1px solid #e9d5ff;
  border-radius: 999px;
  background-color: #2e2440;
  color: #f3e8ff;
  font-size: 0.64rem;
  font-weight: 800;
  line-height: 1.2;
  white-space: nowrap;
}

.machine-body {
  display: flex;
  flex-direction: column; /* Stack comments and drop zones vertically */
}

.employee-drop-container {
  display: flex;
  flex-direction: column;
  margin-top: 10px;
}

.operator-drop-area {
  border: 2px dashed #ccc;
  padding: 10px;
  margin-top: 5px;
  text-align: center;
  color: whitesmoke; /* Change the font color of the operator text */
}

.boxer-drop-area {
  border: 2px dashed #ccc;
  padding: 10px;
  margin-top: 5px;
  text-align: center;
  color: whitesmoke; /* Change the font color of the boxer text */
  margin-bottom: 10px;
}

.wo-ghost {
  opacity: 0.35;
}


.wo-drag {
  cursor: grabbing;
}

.invalid-drop-overlay {
  position: fixed;          /* key: viewport coords match clientX/clientY */
  transform: translate(-50%, -50%);
  font-size: 72px;          /* make it BIG */
  z-index: 999999;
  pointer-events: none;     /* never blocks dragging */
  filter: drop-shadow(0 6px 10px rgba(0,0,0,0.6));
}

.machine-hours-modal {
  max-width: 820px;
}

.machine-hours-label {
  display: block;
  margin-bottom: 6px;
  color: #cbbfbf;
  font-weight: 700;
}

.machine-hours-input {
  width: 120px;
  background-color: #1f2933;
  color: #e5e7eb;
  border: 1px solid #555;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 1rem;
  outline: none;
}

.machine-hours-input:hover {
  border-color: #93c5fd;
}

.machine-hours-input:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 1px rgba(96, 165, 250, 0.4);
}

.machine-hours-actions {
  display: flex;
  gap: 10px;
  margin-top: 18px;
}

.machine-hours-error {
  color: #ff5c5c;
  margin-top: 10px;
}

.machine-hours-success {
  color: #56d364;
  margin-top: 10px;
}

.machine-hours-section {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #374151;
}

.machine-hours-section:first-of-type {
  margin-top: 8px;
}

.machine-hours-section h4 {
  margin: 0 0 6px 0;
  color: #e5e7eb;
  font-size: 1rem;
}

.machine-hours-help {
  margin: 0 0 12px 0;
  color: #9ca3af;
  font-size: 0.82rem;
  line-height: 1.35;
}

.machine-hours-normal-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.machine-capacity-section {
  margin-top: 22px;
}

.machine-capacity-table {
  margin-top: 10px;
}

.machine-capacity-table th,
.machine-capacity-table td {
  vertical-align: middle;
}

.machine-capacity-input {
  width: 82px;
  background-color: #1f2933;
  color: #e5e7eb;
  border: 1px solid #555;
  border-radius: 6px;
  padding: 5px 6px;
  font-size: 0.9rem;
  outline: none;
}

.machine-capacity-input:hover {
  border-color: #93c5fd;
}

.machine-capacity-input:focus {
  border-color: #60a5fa;
  box-shadow:
    0 0 0 1px
    rgba(96, 165, 250, 0.4);
}

.machine-capacity-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.capacity-source-badge {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}

.capacity-source-normal {
  background-color: #374151;
  color: #d1d5db;
  border: 1px solid #6b7280;
}

.capacity-source-override {
  background-color: #78350f;
  color: #fde68a;
  border: 1px solid #d97706;
}

.machine-hours-empty {
  color: #9ca3af;
  margin-top: 8px;
}

/* 🔹 Pablo-the-Elder style modal overlay */
.shortage-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);  /* match .popup-overlay */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
}

/* 🔹 Dark Pablo panel (like pending-popup-content) */
.shortage-modal {
  background-color: #0b0101;
  color: #cbbfbf;
  padding: 10px 20px;
  border-radius: 8px;
  max-width: 900px;
  width: 95vw;
  max-height: 90vh;
  overflow: auto;
  border: 1px solid #888;
  font-family: Garamond, serif;
}

/* Header bar + close button */
.shortage-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  border-bottom: 1px solid #444;
  padding-bottom: 0.5rem;
}

.shortage-modal-header h3 {
  margin: 0;
  font-family: 'Cormorant Garamond', Garamond, serif;
  font-size: 22px;
  color: #cbbfbf;
}

/* Table styling inside dark modal */
.shortage-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.5rem;
  font-size: 0.85rem;
}

.shortage-table th,
.shortage-table td {
  border: 1px solid #333;
  padding: 0.25rem 0.4rem;
  text-align: left;
}

.shortage-table th {
  background-color: #1f2933;
  color: #e5e7eb;
}

.shortage-table td {
  background-color: #111827;
  color: #e5e7eb;
}

/* Close button in the corner */
.close-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1.1rem;
  color: #cbbfbf;
}

.close-btn:hover {
  color: #ffffff;
}

.shortage-indicator-group {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  flex-wrap: nowrap;
}

/* Single-letter shortage indicator */
.shortage-indicator-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 24px;
  min-width: 24px;
  height: 24px;
  padding: 0;

  border: 1px solid #fca5a5;
  border-radius: 50%;

  background-color: #5b1111;
  color: #f9fafb;

  font-size: 0.75rem;
  font-weight: 800;
  line-height: 1;

  cursor: pointer;
  text-align: center;
}

/* WIP shortages: Core and Jacket */
.shortage-indicator-btn.shortage-indicator-wip {
  background-color: #f5c542;
  border-color: #ffe082;
  color: #111111;
}

.shortage-indicator-btn.shortage-indicator-wip:hover {
  background-color: #ffd95a;
  border-color: #fff0a6;
  color: #111111;
}

/* Bx needs extra room while retaining the circled-badge appearance */
.shortage-indicator-btn.shortage-indicator-wide {
  width: 32px;
  min-width: 32px;
  border-radius: 999px;
  font-size: 0.7rem;
}

.shortage-indicator-btn:hover {
  background-color: #7a1b1b;
  border-color: #fecaca;
  transform: scale(1.06);
}

.shortage-indicator-btn:active {
  transform: scale(0.95);
}

/* Make an unmapped material obvious during rollout */
.shortage-indicator-btn.shortage-indicator-unmapped {
  background-color: #6b4f00;
  border-color: #facc15;
  color: #fff7cc;
}

.wo-execution-flags {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  gap: 1px;
  margin-left: 6px;

  /* Keep this little control stack from growing */
  flex: 0 0 42px;
}

.wo-execution-flag {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;

  gap: 2px;

  width: 42px;
  height: 15px;
  min-height: 15px;
  box-sizing: border-box;

  padding: 0 3px;

  border: 1px solid #94a3b8;
  border-radius: 3px;

  background: #334155;
  color: #f8fafc;

  font-size: 9px;
  font-weight: 700;
  line-height: 1;

  cursor: pointer;
  white-space: nowrap;
  user-select: none;
}

.wo-execution-flag input {
  width: 9px;
  height: 9px;
  margin: 0;
  cursor: pointer;
}

.wo-execution-flag.active {
  background: #0f766e;
  border-color: #5eead4;
  color: #ffffff;
}

.wo-execution-flag:hover {
  border-color: #e2e8f0;
}

.forecast-button {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
}

/* Forecast pill */
.forecast-btn {
  background-color: #1f3a5f;          /* cool blue */
  color: #e5e7eb;
  border: 1px solid #93c5fd;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 0.1rem 0.5rem;
  cursor: pointer;
  line-height: 1;
  min-width: 1.4rem;
  text-align: center;
}

/* Hover: brighter info state */
.forecast-btn:hover {
  background-color: #2563eb;
  border-color: #bfdbfe;
}

/* Active press */
.forecast-btn:active {
  transform: scale(0.95);
}

.notes-btn {
  border-radius: 10px;
  margin-bottom: 20px;
  margin-left: 10px;
}

/* 🔧 Machine item/day override control */
.machine-override {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 6px 0 12px 10px;   /* under Notes, before Operator */
  font-size: 0.75rem;
}

/* Label: quiet, descriptive */
.override-label {
  color: #cbbfbf;
  font-weight: 600;
  opacity: 0.85;
  white-space: nowrap;
}

/* Dropdown styling */
.override-select {
  background-color: #1f2933;
  color: #e5e7eb;
  border: 1px solid #555;
  border-radius: 6px;
  padding: 2px 6px;
  font-size: 0.75rem;
  cursor: pointer;
  outline: none;
}

/* Hover / focus = subtle confidence */
.override-select:hover {
  border-color: #93c5fd;
}

.override-select:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 1px rgba(96, 165, 250, 0.4);
}

/* When Default is selected */
.override-select option[value=""] {
  color: #9ca3af;
}

.machine-load-backdrop {
  background: rgba(0, 0, 0, 0.05);
}

.run-drag-handle {
  position: absolute;          /* ✅ NEW */
  top: 4px;
  left: 6px;
  cursor: grab;
  user-select: none;
  opacity: 0.75;
  font-size: 14px;
  line-height: 1;
}
.run-drag-handle:active {
  cursor: grabbing;
}

.run-edit-icon {
  position: absolute;          /* ✅ NEW */
  top: 4px;                    /* ✅ adjust to taste */
  right: 6px;                  /* ✅ adjust to taste */
  font-size: 13px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease;
  line-height: 1;
}

.day-box:hover .run-edit-icon {
  opacity: 0.85;
}

.run-edit-icon:hover {
  opacity: 1;
}

.resources-summary {
  color: #ffffff; /* white text */
  font-size: 0.9rem;
}

.resources-summary strong {
  color: #ffffff; /* ensure the dash/name is also white */
}

.resources-block {
  margin-bottom: 10px; /* space before Hours / Load */
}

.resources-summary {
  margin-top: 6px; /* space under Resources button */
}

.lock-btn {
  width: 36px;
  height: 29.45px;
  border-radius: 10px;
  border: 1px solid #444;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  margin-bottom: 20px;
}

.lock-green {
  background: #2ea043;
  color: white;
}

.lock-red {
  background: #c53030;
  color: white;
}

.lock-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.wo-label {
  position: relative;
  display: inline-block;   /* IMPORTANT: does not change flex sizing */
}

.wo-lock-dot {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 10px;            /* pushes dot to the right of the WO text */
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #d32f2f;
}

.qoh-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 54px;
  padding: 2px 7px;
  margin-left: 6px;
  border-radius: 8px;
  background: #e6edf3;
  color: #111;
  border: 1px solid #9aa4ad;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}


.machine-totals {
  margin: 2px 10px 8px 20px;
  padding: 5px;
}

.machine-total-row {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}

.machine-total-label {
  display: flex;
  align-items: center;
  justify-content: flex-end;

  width: 150px;
  min-width: 150px;
  margin-left: 10px;
  margin-right: 20px;
  padding-right: 8px;

  color: #ffffff;
  font-size: 0.82rem;
  font-weight: 800;
}

.machine-total-box {
  width: 50px;
  height: 28px;
  padding: 0 18px;
  margin-right: 5px;

  display: flex;
  align-items: center;
  justify-content: center;

  border: 1px solid #ccc;
  border-radius: 5px;

  color: #ffffff;
  font-size: 0.78rem;
  font-weight: 800;
  white-space: nowrap;
}

.qt-total-box {
  background-color: #1f4f91;
}

.st-total-box {
  background-color: #4b5563;
}

.st-total-box.has-shortage {
  background-color: #9b1c1c;
}

.machine-item-number {
  display: inline-block;
  align-self: center;
  width: fit-content;
  max-width: 100%;

  padding: 1px 4px;
  border-radius: 3px;

  background-color: rgba(255, 255, 255, 0.65);
  border: 1px solid rgba(17, 24, 39, 0.2);

  font-weight: 800;
  line-height: 1.1;
  text-align: center;

  text-shadow: none;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.15);
}

/* R-4.2 */
.item-r42 {
  color: #2563eb !important;
}

/* R-6 */
.item-r6 {
  color: #92400e !important;
}

/* R-8 */
.item-r8 {
  color: #15803d !important;
}

/* Mobile Home */
.item-mobile-home {
  color: #dc2626 !important;
}

/* Carton product: item number does not contain B */
.item-carton {
  font-style: italic !important;
}

.keep-position-flag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}

.keep-position-flag input {
  margin: 0;
  cursor: pointer;
}


/* =============================================================
   PABLO 2026 SCHEDULE GRID — styling only
   Retains the original SortableJS wrappers, handlers and events.
   ============================================================= */
.scrollable-machines-container {
  width: 100%;
  height: calc(100vh - 112px);
  max-height: none;
  min-height: 480px;
  margin: 0;
  padding: 9px;
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: #3c5978 #101d2d;
  border: 1px solid #2c435a;
  border-radius: 13px;
  background: linear-gradient(160deg, #132338 0%, #101d2e 100%);
  color: #eaf3ff;
  font-family: Inter, 'Segoe UI', Roboto, Arial, sans-serif;
}
.scrollable-machines-container * { box-sizing: border-box; }
.scrollable-machines-container .stacked-machines { min-width: 980px; }
.machine-row {
  display: flex;
  align-items: stretch;
  gap: 10px;
  width: 100%;
  margin: 0 0 10px;
  padding: 9px;
  border: 1px solid #2c4159;
  border-radius: 11px;
  background: #1b2a3f !important;
  color: #eaf3ff;
  transition: border-color .15s ease, background-color .15s ease;
}
.machine-row:hover { background: #20324a !important; border-color: #4a6280; }
.machine-header {
  flex: 0 0 184px;
  width: 184px;
  max-width: 184px;
  display: flex;
  flex-direction: column;
  padding: 3px 5px 4px;
  border-right: 1px solid #34475d;
}
.machine-name {
  margin: 0 0 9px;
  color: #f1f6ff;
  font-size: 17px;
  line-height: 1.2;
  font-weight: 750;
  letter-spacing: .025em;
}
.machine-body { min-width: 0; }
.employee-drop-container { margin: 0; gap: 8px; }
.notes-btn {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 30px;
  padding: 6px 9px;
  margin: 0;
  border-radius: 7px;
  border: 1px solid #3d5773;
  background: #263a53;
  color: #e3f0ff;
  cursor: pointer;
  font-size: 12px;
  font-weight: 650;
  line-height: 1;
}
.notes-btn:hover:not(:disabled) { background:#365575; border-color:#7ba6d3; }
.notes-btn:disabled { opacity:.4; cursor:not-allowed; }
.machine-override { display: flex; margin: 0; gap: 6px; align-items: center; justify-content: space-between; }
.override-label { color:#aabfd5; font-size:11px; opacity:1; }
.override-select {
  width:90px;
  min-height:27px;
  background:#16283c;
  color:#f0f5ff;
  border:1px solid #405a74;
  border-radius:7px;
  font-size:11px;
}
.resources-block { margin: 0; }
.resources-summary { margin:6px 0 3px; color:#b4c8de; font-size:11px; line-height:1.45; }
.resources-summary strong { color:#ecf6ff; font-weight:650; }
.lock-btn { margin:0; min-height:30px; width:36px; height:30px; border-radius:8px; }
.lock-red { background:#892b3a; border:1px solid #e46b7b; }
.lock-green { background:#145844; border:1px solid #3caf8a; }
.machine-order-list { flex: 1 1 0; min-width:0; padding:0 1px; }
.machine-row .work-order {
  display: block;
  width:100%;
  min-width:0;
  min-height:74px;
  margin: 0 0 5px;
  padding: 6px 7px;
  border: 1px solid #365875;
  border-left: 4px solid #26b7cf;
  border-radius:8px;
  background:#253e55;
  color:#f5faff;
  box-shadow:0 2px 5px #07132035;
  cursor:grab;
}
.machine-row .work-order:hover { background:#2c4a63; border-color:#4b88ae; }
.machine-row .work-order.pending-work-order {
  border:1px dashed #bd91f5;
  border-left:4px solid #a78bfa;
  background:#392c50;
  box-shadow: none;
}
.machine-row .work-order.unreviewed-work-order:not(.pending-work-order) {
  border:1px solid #ce8948;
  border-left:4px solid #f7b84f;
  background:#5a3c24;
  box-shadow:none;
}
.machine-row .work-order.unreviewed-work-order:not(.pending-work-order):hover { background:#6c4b2d; border-color:#ffc178; }
.machine-row .work-order.pending-work-order:hover { background:#44365d; border-color:#dcc2ff; }
.machine-row .work-order .order-info {
  display: grid;
  grid-template-columns: 140px minmax(0, 1fr) 52px 54px;
  align-items: center;
  gap:7px;
  width:100%;
  min-width:0;
  margin:0;
}
.order-details {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap:3px;
  width:auto;
  min-width:0;
  margin:0;
  flex:initial;
  font-size:11px;
  line-height:1.2;
  color:#dcecff;
}
.machine-item-number {
  align-self:flex-start;
  max-width:100%;
  padding:3px 5px;
  font-size:11px;
  font-weight:800;
  border:1px solid #bfd7eb;
  border-radius:5px;
  color:#165e73;
  background:#dceef4;
  text-overflow:ellipsis;
  overflow:hidden;
  white-space:nowrap;
  box-shadow:none;
}
.item-r42 { color:#2548a9 !important; }
.item-r6 { color:#925115 !important; }
.item-r8 { color:#077351 !important; }
.item-mobile-home { color:#ba3046 !important; }
.wo-label { display:inline-flex; align-items:center; font-weight:550; }
.wo-lock-dot { position:static; transform:none; display:inline-block; margin-left:7px; width:8px; height:8px; background:#ff555f; }
.pending-order-badge { margin:0; padding:2px 6px; font-size:10px; background:#332448; }
.keep-position-flag { flex-wrap:wrap; white-space:normal; font-size:10px; }
.day-boxes {
  display:grid;
  grid-template-columns:repeat(var(--planning-days, 7),minmax(0,1fr));
  flex:none;
  width:100%;
  min-width:0;
  gap:5px;
  margin:0;
}
.day-box {
  position:relative;
  width:auto;
  min-width:0;
  height:64px;
  margin:0;
  padding:17px 3px 7px;
  display:flex;
  flex-direction:column;
  justify-content:center;
  align-items:center;
  gap:5px;
  border:1px solid #4a6885;
  border-radius:6px;
  background:#37536d;
  color:#eaf4ff;
  white-space:nowrap;
  cursor:pointer;
  overflow:hidden;
  transition:filter .12s,border-color .12s;
}
.day-box:hover { filter:brightness(1.14); border-color:#77a1ce; background:#416381; }
.day-box.has-qty,
.day-box.has-qty:hover { background:#1766eb; border-color:#599cff; color:#fff; }
.day-box.fg-short-covered,
.day-box.fg-short-covered:hover { background:#f6c444; color:#111827; border-color:#ffda83; }
.day-box.fg-short-uncovered,
.day-box.fg-short-uncovered:hover { background:#d73a3f; border-color:#ff868d; color:#fff; }
.day-box.fg-short-uncovered .day-label,
.day-box.fg-short-uncovered .day-value { color:#fff; }
.day-box.fg-short-covered .day-label,
.day-box.fg-short-covered .day-value { color:#172230; }
.day-label { font-size:10px; color:inherit; opacity:.86; line-height:1; }
.day-value { font-size:15px; font-weight:800; color:inherit; line-height:1; }
.run-drag-handle { top:4px; left:5px; opacity:.8; font-size:13px; line-height:1; }
.run-edit-icon { top:4px; right:3px; opacity:0; font-size:12px; }
.fg-short-corner { bottom:3px; right:3px; font-size:10px; padding:2px 4px; border-radius:9px; }
.must-badge {
  position:absolute;
  left:4px;
  bottom:4px;
  width:6px;
  height:6px;
  padding:0;
  border-radius:50%;
  background:#fff2a1;
  box-shadow:0 0 0 1px #37415166;
}
.shortage-indicator-group { grid-column:3; margin:0; display:flex; flex-wrap:wrap; justify-content:center; gap:3px; }
.shortage-indicator-btn { width:23px; height:23px; min-width:23px; border-radius:50%; background:#85242c; border-color:#ed8686; }
.shortage-indicator-btn.shortage-indicator-wip { background:#f2c94c; color:#1e293b; }
.wo-execution-flags { grid-column:4; gap:4px; margin:0; width:54px; flex:initial; }
.wo-execution-flag { width:51px; height:17px; padding:0 4px; border-radius:4px; border-color:#536d89; background:#21354c; font-size:10px; }
.wo-execution-flag input { width:10px; height:10px; accent-color:#0acc98; }
.wo-execution-flag.active { background:#116b65; border-color:#35bcb2; }
.machine-row.date-machine-row,
.machine-row.date-machine-row:hover {
  position:sticky;
  top:-9px;
  z-index:40;
  padding:9px;
  min-height:85px;
  background:#142439 !important;
  border-color:#304861;
  box-shadow:0 4px 16px #081522a8;
}
.date-machine-row .machine-header { justify-content:center; }
.date-machine-row .machine-name { margin:0; font-size:14px; }
.machine-row .date-work-order,
.machine-row .date-work-order:hover {
  min-height:64px;
  margin:0;
  padding:0 7px;
  background:transparent;
  border:1px solid transparent;
  box-shadow:none;
  cursor:default;
}
.date-order-placeholder { min-height:1px; }
.date-day-box,
.date-day-box:hover {
  height:62px;
  padding:5px 3px;
  background:#1c2d43;
  border:1px solid #3b536e;
  cursor:default;
  pointer-events:none;
  gap:6px;
}
.date-day-box:first-child,
.date-day-box:first-child:hover { background:#1366ed; border-color:#5699fa; }
.planning-date-day { color:#f0f6ff; font-size:11px; }
.planning-date-value { color:#d1e4ff; font-size:12px; margin:0; }
.machine-totals { margin:3px 0 4px; padding:5px 7px 0; }
.machine-total-qty { margin:0 0 8px; color:#d8e8fb; font-size:12px; font-weight:650; }
.machine-total-qty strong { color:white; }
.machine-total-row {
  display:grid;
  grid-template-columns:140px minmax(0,1fr) 52px 54px;
  align-items:center;
  gap:7px;
  margin-bottom:5px;
}
.machine-total-label {
  justify-content:flex-end;
  width:100%;
  min-width:0;
  margin:0;
  padding:0 6px;
  color:#c0d4e9;
  font-size:11px;
  font-weight:750;
}
.machine-total-box {
  display:flex;
  align-items:center;
  justify-content:center;
  width:auto;
  min-width:0;
  height:27px;
  padding:0 3px;
  margin:0;
  font-size:11px;
  border-radius:5px;
  border:1px solid #4a6f9a;
}
.qt-total-box { background:#16448b; }
.st-total-box { background:#283b52; }
.st-total-box.has-shortage { background:#9c2a33; border-color:#de6770; }
.wo-ghost { opacity:.35; }
.wo-chosen { outline:2px solid #84b9ff; }
.wo-drag { cursor:grabbing; }
.shortage-modal { background:#15263c; color:#eaf2ff; font-family:Inter,'Segoe UI',sans-serif; border-radius:14px; border:1px solid #3b5671; }
.shortage-modal-header h3 { color:#eef5ff; font-family:Inter,'Segoe UI',sans-serif; }
.machine-load-backdrop { background:rgba(2,8,17,.75); }
@media (max-width:1400px) {
  .scrollable-machines-container { height:calc(100vh - 106px); }
}

</style>
